/**
 * Amethyst · Behavioral Analytics & Social Coach — admin console (Round 64).
 * The engagement numbers it already kept (actives, retention, funnel, churn),
 * plus its new job: one coaching card per active member (what they do in lives,
 * chat, posts and games — strengths, growth areas, one thing to try), a weekly
 * behavior report with platform suggestions, and notes to members that wait in
 * Decisions (off by default).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BarChart3, RefreshCw, Sparkles, FileText, Send, Search, UserCheck, MessageSquare, Settings, Play } from 'lucide-react';

const BASE = '/api/admin/workforce/amethyst';
const errText = (e, fallback) => e?.response?.data?.detail || fallback;
const STATUS = { draft: 'Draft', proposed: 'In Decisions', sent: 'Note sent', dismissed: 'Dismissed' };
const STATUS_CLS = { draft: 'bg-gray-100 text-gray-700', proposed: 'bg-amber-100 text-amber-800', sent: 'bg-emerald-100 text-emerald-800', dismissed: 'bg-gray-100 text-gray-500' };
const NUDGE_HELP = {
  off: 'Cards are for the admins only — nothing is sent to members.',
  propose: 'Each note waits in Decisions; a person sends it.',
  auto: 'Notes go out by themselves after Lapis reads them (one per member per cooldown).',
};
const NUM_FIELDS = [
  ['window_days', 'Days a card looks back'], ['refresh_days', 'Days between re-reads'], ['members_per_run', 'Members per run'],
  ['claude_per_run', 'Claude reads per run'], ['claude_per_day', 'Claude reads per day'], ['nudge_cooldown_days', 'Days between notes to one member'],
  ['nudges_per_day', 'Notes per day'],
];

const Tile = ({ label, val, sub }) => (
  <div className="bg-white border rounded-xl p-3">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-xl font-bold text-gray-900">{val ?? '—'}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const Num = ({ label, numbers }) => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
    {numbers.map(([k, v]) => <span key={k}><span className="text-gray-400">{k}</span> {v}</span>)}
  </div>
);

const AmethystPage = () => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cards, setCards] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`${BASE}/overview`);
      setData(r.data);
      setForm((f) => f || r.data.config);
    } catch (e) { toast.error('Could not load Amethyst'); }
  }, [api]);
  const loadCards = useCallback(async () => {
    try {
      const r = await api.get(`${BASE}/members`, { params: { q, status, limit: 60 } });
      setCards(r.data.cards || []);
    } catch (e) { /* the overview error already toasted */ }
  }, [api, q, status]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(loadCards, q ? 300 : 0); return () => clearTimeout(t); }, [loadCards, q]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); await loadCards(); return r; }
    catch (e) { toast.error(errText(e, 'That did not work')); return null; }
    finally { setBusy(''); }
  };
  const saveSettings = () => call('settings', () => api.post(`${BASE}/config`, form), 'Saved');

  if (!data) return <div className="p-6 text-sm text-gray-500">Loading Amethyst…</div>;
  const cfg = data.config;
  const counts = data.counts || {};
  const eng = (data.insights || []).find((i) => i.key === 'engagement')?.data || {};
  const rep = data.report;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl" data-testid="amethyst-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-purple-600" /> Amethyst · Behavioral Analytics &amp; Social Coach</h2>
          <p className="text-sm text-gray-500">
            {counts.fresh_week || 0} members read this week · {counts.waiting || 0} waiting in Decisions · notes to members: <b>{cfg.nudges}</b>
            {data.llm?.available ? ` · Claude on (${data.llm.model || 'default model'}, $${(data.llm.month_cost_usd || 0).toFixed(2)} this month)` : ' · Claude off — rules only until a key is set'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => call('analyze', () => api.post(`${BASE}/analyze-now`), (r) => `Read ${r.data.profiled} member(s)${r.data.report ? ' · report filed' : ''}`)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm" disabled={!!busy} data-testid="amethyst-analyze-now"><Sparkles className="w-4 h-4" /> Analyze now</button>
          <button onClick={() => call('report', () => api.post(`${BASE}/report-now`), 'Report filed in Decisions')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm" disabled={!!busy} data-testid="amethyst-report-now"><FileText className="w-4 h-4" /> Report now</button>
          <button onClick={() => call('run', () => api.post('/api/admin/workforce/agents/AMETHYST/run'), 'Amethyst ran — numbers and cards refreshed')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm" disabled={!!busy} title="The full run: engagement numbers, then the coach"><Play className="w-4 h-4" /> Run Amethyst</button>
          <button onClick={() => { load(); loadCards(); }} className="p-2 rounded-lg border text-gray-600" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* the numbers it already kept */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="amethyst-engagement">
        <Tile label="Members" val={eng.members} />
        <Tile label="Daily actives" val={eng.dau} sub={eng.mau ? `${Math.round((eng.dau / eng.mau) * 100)}% of monthly` : null} />
        <Tile label="Weekly actives" val={eng.wau} />
        <Tile label="Monthly actives" val={eng.mau} />
        <Tile label="D7 retention" val={eng.d7_retention != null ? `${Math.round(eng.d7_retention * 100)}%` : null} sub={eng.cohort != null ? `cohort ${eng.cohort}` : null} />
        <Tile label="At churn risk" val={eng.churn_risk} sub="quiet 7–30 days" />
      </div>

      {/* settings */}
      <div className="bg-white border rounded-xl p-4">
        <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Settings className="w-4 h-4" /> How Amethyst coaches</p>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="text-sm text-gray-700">Notes to members
            <select value={form?.nudges || 'off'} onChange={(e) => setForm((f) => ({ ...f, nudges: e.target.value }))} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full bg-white" data-testid="amethyst-nudges">
              {(data.modes || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <span className="block text-xs text-gray-400 mt-1">{NUDGE_HELP[form?.nudges] || ''}</span>
          </label>
          {NUM_FIELDS.map(([k, label]) => (
            <label key={k} className="text-sm text-gray-700">{label}
              <input type="number" value={form?.[k] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className="mt-1 block border rounded-lg px-2 py-1.5 text-sm w-full" />
            </label>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button onClick={saveSettings} disabled={busy === 'settings'} className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold">Save</button>
          <span className="text-xs text-gray-500">Only a member's own words are read (handles, links, emails and numbers stripped). Lapis reads every note before it goes out. Claude costs are metered under Amethyst's own cap.</span>
        </div>
      </div>

      {/* the week's report */}
      <div className="bg-white border rounded-xl p-4" data-testid="amethyst-report">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><FileText className="w-4 h-4" /> Behavior report {rep ? `· ${rep.week}` : ''}</p>
          {rep && <span className="text-xs text-gray-500">{rep.source === 'claude' ? 'written with Claude' : 'from the numbers'} · {STATUS[rep.status] || rep.status}</span>}
        </div>
        {!rep ? (
          <p className="text-sm text-gray-500 mt-2">No report yet — it is filed once a week when enough members have been read, or press Report now.</p>
        ) : (
          <div className="mt-2 space-y-3 text-sm">
            <p className="text-gray-800">{rep.summary}</p>
            {rep.patterns?.length > 0 && (
              <div><p className="text-xs uppercase tracking-wide text-gray-500">Patterns</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-0.5">{rep.patterns.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
            )}
            {rep.suggestions?.length > 0 && (
              <div><p className="text-xs uppercase tracking-wide text-gray-500">What the platform could change</p>
                <ul className="list-disc pl-5 text-gray-700 space-y-0.5">{rep.suggestions.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
            )}
            {rep.product_notes?.length > 0 && (
              <div><p className="text-xs uppercase tracking-wide text-gray-500">Notes from the cards</p>
                <ul className="list-disc pl-5 text-gray-600 space-y-0.5">{rep.product_notes.map((p, i) => <li key={i}>{p}</li>)}</ul></div>
            )}
            <button onClick={() => navigate('/decisions')} className="text-xs text-purple-700 hover:underline">Open in Decisions →</button>
          </div>
        )}
      </div>

      {/* member cards */}
      <div className="bg-white border rounded-xl p-4" data-testid="amethyst-cards">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><UserCheck className="w-4 h-4" /> Coaching cards <span className="text-gray-400 font-normal">· {counts.cards || 0} members</span></p>
          <div className="flex items-center gap-2">
            <div className="relative"><Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or @handle" className="border rounded-lg pl-8 pr-2 py-2 text-sm" data-testid="amethyst-search" /></div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="border rounded-lg px-2 py-2 text-sm bg-white">
              <option value="">Every card</option>
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
        {cards.length === 0 ? (
          <p className="text-sm text-gray-500 mt-3">No cards yet. Amethyst reads the members who were on GEMS this week on its next run — or press Analyze now.</p>
        ) : (
          <div className="mt-3 grid lg:grid-cols-2 gap-3">
            {cards.map((c) => {
              const p = c.profile || {};
              const h = p.hosted || {}; const w = p.watched || {}; const ch = p.chat || {}; const ct = p.content || {}; const g = p.games || {}; const gf = p.gifts || {}; const so = p.social || {};
              return (
                <div key={c.id} className="border rounded-xl p-3 space-y-2" data-testid={`amethyst-card-${c.user_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {c.picture ? <img src={c.picture} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">{(c.name || '?')[0]}</div>}
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{c.name} <span className="text-gray-400 font-normal">@{c.handle}</span></div>
                        <div className="text-xs text-gray-500">{p.member_days != null ? `member ${p.member_days}d · ` : ''}read {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ''} · {c.source === 'claude' ? 'Claude' : 'rules'}</div>
                      </div>
                    </div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || ''}`}>{STATUS[c.status] || c.status}</span>
                  </div>
                  <Num numbers={[['hosted', `${h.lives || 0} (${Math.round(h.minutes || 0)} min, ${Object.entries(h.by_client || {}).map(([k, v]) => `${k} ${v}`).join('/') || '—'})`], ['watched', `${w.lives || 0}`],
                    ['chat', `${ch.lines || 0} lines / ${ch.rooms || 0} rooms`], ['posts', `${ct.posts || 0} + ${ct.comments || 0} comments`], ['games', g.played || 0], ['gifts', gf.sent || 0], ['followed', so.followed || 0]]} />
                  <p className="text-sm font-medium text-gray-900">{c.headline}</p>
                  {(c.strengths || []).map((s, i) => <p key={i} className="text-sm text-emerald-800"><b>{s.title}.</b> {s.detail}</p>)}
                  {(c.growth || []).map((gr, i) => <p key={i} className="text-sm text-amber-900"><b>{gr.observation}</b> → {gr.suggestion}</p>)}
                  {(c.experience_tips || []).map((t, i) => <p key={i} className="text-xs text-gray-600">· {t}</p>)}
                  {(c.samples || []).length > 0 && (
                    <button onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))} className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {open[c.id] ? 'Hide' : 'What they said'}</button>
                  )}
                  {open[c.id] && <ul className="text-xs text-gray-600 pl-4 list-disc">{c.samples.slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}{(c.post_samples || []).map((s, i) => <li key={`p${i}`}>post: {s}</li>)}</ul>}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <button onClick={() => call(`re:${c.id}`, () => api.post(`${BASE}/members/${c.user_id}/analyze`), 'Read again')} className="px-2.5 py-1.5 rounded-lg border text-xs" disabled={!!busy}>Analyze again</button>
                    <button onClick={() => call(`n:${c.id}`, () => api.post(`${BASE}/cards/${c.id}/propose`), (r) => (r.data.mode === 'auto' ? 'Note sent' : 'Note filed in Decisions'))}
                      className="px-2.5 py-1.5 rounded-lg bg-purple-600 text-white text-xs inline-flex items-center gap-1 disabled:opacity-50" disabled={!!busy || cfg.nudges === 'off' || c.status === 'sent'}
                      title={cfg.nudges === 'off' ? 'Switch notes to propose or auto first' : ''} data-testid={`amethyst-propose-${c.user_id}`}><Send className="w-3 h-3" /> {cfg.nudges === 'auto' ? 'Send note' : 'Propose note'}</button>
                    {c.status !== 'dismissed' && <button onClick={() => call(`d:${c.id}`, () => api.post(`${BASE}/cards/${c.id}/dismiss`), 'Dismissed')} className="px-2.5 py-1.5 rounded-lg border text-xs text-gray-600" disabled={!!busy}>Dismiss</button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(data.logs || []).length > 0 && (
        <div className="bg-white border rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-900">Recent activity</p>
          <ul className="mt-2 text-xs text-gray-600 space-y-1">{data.logs.slice(0, 10).map((l, i) => <li key={i}><span className="text-gray-400">{new Date(l.timestamp).toLocaleString()}</span> {l.message}</li>)}</ul>
        </div>
      )}
    </div>
  );
};

export default AmethystPage;
